/**
 * 透過フレームレスウィンドウマネージャ (TransparentWindowManager)
 * aidlc-docs/construction/desktop_ambient_platform/docs/transparent_window_manager_component.md 準拠
 */

import type { DisplayWorkArea, IPlatformBridgeAdapter, WindowBounds } from './bridges/IPlatformBridgeAdapter';

export interface WindowConfig {
  /** 初期ウィンドウ幅 (px, 既定値: 320) */
  width?: number;
  /** 初期ウィンドウ高さ (px, 既定値: 320) */
  height?: number;
  /** 初期表示X座標 */
  initialX?: number;
  /** 初期表示Y座標 */
  initialY?: number;
  /** 初期状態で最前面ピン留めするか (既定値: true) */
  alwaysOnTop?: boolean;
}

export class TransparentWindowManager {
  private static readonly MIN_WIDTH = 120;
  private static readonly MIN_HEIGHT = 120;
  private static readonly MIN_VISIBLE_PIXELS = 50; // 画面外ロスト防止用最小露出幅/高

  private readonly bridge: IPlatformBridgeAdapter;
  private isTransparentState: boolean = true;
  private isAlwaysOnTopState: boolean = false;
  private currentBounds: WindowBounds = {
    x: 100,
    y: 100,
    width: 320,
    height: 320,
  };
  private isInitialized: boolean = false;

  constructor(bridge: IPlatformBridgeAdapter) {
    this.bridge = bridge;
  }

  /**
   * ウィンドウの初期化
   */
  public async init(config: WindowConfig = {}): Promise<void> {
    const width = Math.max(TransparentWindowManager.MIN_WIDTH, config.width ?? 320);
    const height = Math.max(TransparentWindowManager.MIN_HEIGHT, config.height ?? 320);

    const initialX = config.initialX ?? this.currentBounds.x;
    const initialY = config.initialY ?? this.currentBounds.y;

    this.currentBounds = {
      x: initialX,
      y: initialY,
      width,
      height,
    };

    const workArea = await this.bridge.getWorkArea();
    this.currentBounds = this.clampToBounds(this.currentBounds, workArea);

    this.isAlwaysOnTopState = config.alwaysOnTop ?? true;

    await this.bridge.setBounds(this.currentBounds);
    await this.bridge.setAlwaysOnTop(this.isAlwaysOnTopState);

    this.isInitialized = true;
  }

  /**
   * 最前面ピン留め表示の切り替え
   */
  public async setAlwaysOnTop(enable: boolean): Promise<void> {
    this.isAlwaysOnTopState = enable;
    await this.bridge.setAlwaysOnTop(enable);
  }

  /**
   * ウィンドウ境界・位置の取得
   */
  public async getBounds(): Promise<WindowBounds> {
    const bounds = await this.bridge.getBounds();
    this.currentBounds = bounds;
    return { ...this.currentBounds };
  }

  /**
   * ウィンドウ境界・位置の設定（最小サイズ制約および画面外飛び出しクランプ適用）
   */
  public async setBounds(newBounds: Partial<WindowBounds>): Promise<void> {
    const width = Math.max(
      TransparentWindowManager.MIN_WIDTH,
      newBounds.width ?? this.currentBounds.width
    );
    const height = Math.max(
      TransparentWindowManager.MIN_HEIGHT,
      newBounds.height ?? this.currentBounds.height
    );
    const x = newBounds.x ?? this.currentBounds.x;
    const y = newBounds.y ?? this.currentBounds.y;

    const rawBounds: WindowBounds = { x, y, width, height };
    const workArea = await this.bridge.getWorkArea();
    const clampedBounds = this.clampToBounds(rawBounds, workArea);

    this.currentBounds = clampedBounds;
    await this.bridge.setBounds(this.currentBounds);
  }

  public isAlwaysOnTop(): boolean {
    return this.isAlwaysOnTopState;
  }

  public isTransparent(): boolean {
    return this.isTransparentState;
  }

  public getIsInitialized(): boolean {
    return this.isInitialized;
  }

  public async destroy(): Promise<void> {
    this.isInitialized = false;
    await this.bridge.setAlwaysOnTop(false);
  }

  /**
   * 画面外飛び出し防止クランプ（最低でも MIN_VISIBLE_PIXELS が有効作業領域内に留まる）
   */
  private clampToBounds(bounds: WindowBounds, workArea: DisplayWorkArea): WindowBounds {
    const minX = workArea.x - bounds.width + TransparentWindowManager.MIN_VISIBLE_PIXELS;
    const maxX = workArea.x + workArea.width - TransparentWindowManager.MIN_VISIBLE_PIXELS;
    const clampedX = Math.max(minX, Math.min(maxX, bounds.x));

    const minY = workArea.y;
    const maxY = workArea.y + workArea.height - TransparentWindowManager.MIN_VISIBLE_PIXELS;
    const clampedY = Math.max(minY, Math.min(maxY, bounds.y));

    return {
      x: clampedX,
      y: clampedY,
      width: bounds.width,
      height: bounds.height,
    };
  }
}
